import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { Container, Row, Col, Card, Form } from "react-bootstrap";
import { MapContainer, TileLayer, Polygon, Marker, Popup, ZoomControl } from "react-leaflet";
import L from "leaflet";
import Spinner from "@components/Spinner/Spinner";
import ServerError from "@components/Error/ServerError";
import { SearchIcon } from "@components/icons";
import { useNavigate } from "react-router-dom";
import { useProjectFilter } from "../../../context/ProjectFilterContext";
import { getAllIrrigationAreas, getIrrigationArea } from "../services/areaCrud";
import { parentTreeIcon, noSensorTreeIcon } from "@helper/marker";
import { GREEN, YELLOW, RED, GREY, BLUE, WHITE } from "@helper/colors";
import "./AreaMapPage.scss";

const DEFAULT_CENTER = [51.1657, 10.4515];

const toLatLng = (value) => {
  if (!value) return null;
  if (Array.isArray(value) && value.length >= 2) {
    const lat = Number(value[0]);
    const lng = Number(value[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
    return null;
  }
  const lat = Number(value.lat);
  const lng = Number(value.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  return null;
};

const getPolygonPositions = (polygon) => {
  if (!polygon) return [];
  const positions = Array.isArray(polygon.positions) ? polygon.positions : [];
  const vertices = Array.isArray(polygon.vertices) ? polygon.vertices : null;
  if (vertices && vertices.length > 0) {
    return vertices.map((index) => toLatLng(positions[index])).filter(Boolean);
  }
  return positions.map((position) => toLatLng(position)).filter(Boolean);
};

const getAreaProjectId = (area) => {
  const projectId = area?.project?.id ?? area?.project_id;
  return Number.isFinite(Number(projectId)) ? Number(projectId) : null;
};

const getAreaTrees = (area) => {
  // API detail payload uses tree_set as authoritative source.
  if (Array.isArray(area?.tree_set) && area.tree_set.length > 0) return area.tree_set;
  if (Array.isArray(area?.trees) && area.trees.length > 0) return area.trees;
  if (Array.isArray(area?.tree_set)) return area.tree_set;
  if (Array.isArray(area?.trees)) return area.trees;
  return [];
};

const HEALTH_COLOR_BY_STATE = {
  0: GREEN,
  1: YELLOW,
  2: RED,
  3: GREY,
  4: WHITE,
  5: BLUE,
};

const HEALTH_SEVERITY = {
  2: 5,
  1: 4,
  3: 3,
  5: 2,
  0: 1,
  4: 1,
};

const getWorstHealthState = (trees) => {
  if (!Array.isArray(trees) || trees.length === 0) return 3;
  let worstState = 3;
  let worstScore = -1;
  trees.forEach((tree) => {
    const state = Number(tree?.health_state);
    const score = HEALTH_SEVERITY[state] ?? -1;
    if (score > worstScore) {
      worstScore = score;
      worstState = state;
    }
  });
  return worstState;
};

export default function AreaMapPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const { selectedProjectId } = useProjectFilter();

  const { data, isLoading, isError } = useQuery(
    ["areas-map", selectedProjectId],
    async () => {
      const areaListRaw = await getAllIrrigationAreas(
        selectedProjectId ? { project_id: selectedProjectId } : {}
      );
      const areaList = Array.isArray(areaListRaw)
        ? areaListRaw
        : areaListRaw?.results || areaListRaw?.data || [];
      const details = await Promise.all(
        areaList.map(async (area) => {
          try {
            return await getIrrigationArea(area.id);
          } catch (error) {
            return area;
          }
        })
      );
      return details;
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const filteredAreas = useMemo(() => {
    const areas = Array.isArray(data) ? data : [];
    const q = searchTerm.trim().toLowerCase();
    return areas.filter((area) => {
      if (selectedProjectId) {
        const areaProjectId = getAreaProjectId(area);
        if (areaProjectId && Number(areaProjectId) !== Number(selectedProjectId)) return false;
      }
      if (!q) return true;
      return String(area?.name || "")
        .toLowerCase()
        .includes(q);
    });
  }, [data, searchTerm, selectedProjectId]);

  const selectedArea = useMemo(
    () => filteredAreas.find((area) => String(area.id) === String(selectedAreaId)) || null,
    [filteredAreas, selectedAreaId]
  );

  useEffect(() => {
    if (!filteredAreas.length) {
      setSelectedAreaId(null);
      return;
    }
    if (
      !selectedAreaId ||
      !filteredAreas.some((area) => String(area.id) === String(selectedAreaId))
    ) {
      setSelectedAreaId(filteredAreas[0].id);
    }
  }, [filteredAreas, selectedAreaId]);

  const mapCenter = useMemo(() => {
    if (selectedArea?.polygon?.centroid) {
      const centroid = toLatLng(selectedArea.polygon.centroid);
      if (centroid) return centroid;
    }
    for (const area of filteredAreas) {
      const polygon = getPolygonPositions(area?.polygon);
      if (polygon.length > 0) return polygon[0];
      const firstTree = getAreaTrees(area).find((tree) => toLatLng(tree?.position));
      if (firstTree) return toLatLng(firstTree.position);
    }
    return DEFAULT_CENTER;
  }, [selectedArea, filteredAreas]);

  useEffect(() => {
    if (!mapRef.current || !selectedArea) return;
    const polygon = getPolygonPositions(selectedArea?.polygon);
    if (polygon.length > 2) {
      const bounds = L.latLngBounds(polygon);
      mapRef.current.fitBounds(bounds, { padding: [30, 30] });
      return;
    }
    const treePositions = getAreaTrees(selectedArea)
      .map((tree) => toLatLng(tree?.position))
      .filter(Boolean);
    if (treePositions.length > 0) {
      const bounds = L.latLngBounds(treePositions);
      mapRef.current.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [selectedArea]);

  if (isLoading) return <Spinner height={"80vh"} />;
  if (isError) return <ServerError />;

  return (
    <div className="area-map-page">
      <Container fluid className="entity-list-page">
        <Row className="g-3">
          <Col lg={4} xl={3}>
            <Card className="h-100">
              <Card.Body className="d-flex flex-column">
                <h6 className="mb-2">{t("areas")}</h6>
                <div className="area-map-search mb-2">
                  <Form.Control
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder={t("search_area")}
                  />
                  <SearchIcon top={0} width="30px" left="5px" />
                </div>
                <div className="area-map-list" role="mobileList">
                  {filteredAreas.length === 0 ? (
                    <div className="small text-muted py-2">{t("no_data_found")}</div>
                  ) : (
                    filteredAreas.map((area) => {
                      const treeCount = getAreaTrees(area).length;
                      const isActive = String(area.id) === String(selectedAreaId);
                      return (
                        <button
                          key={area.id}
                          type="button"
                          className={`area-map-item ${isActive ? "active" : ""}`}
                          onClick={() => setSelectedAreaId(area.id)}
                        >
                          <div className="fw-semibold text-truncate">{area.name}</div>
                          <small className="text-muted">
                            {treeCount} {t("trees")}
                          </small>
                        </button>
                      );
                    })
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={8} xl={9}>
            <Card className="h-100 area-map-card">
              <Card.Body className="p-0">
                <MapContainer
                  center={mapCenter}
                  zoom={13}
                  zoomControl={false}
                  className="area-map"
                  whenCreated={(mapInstance) => {
                    mapRef.current = mapInstance;
                  }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  <ZoomControl position="bottomleft" />
                  {filteredAreas.map((area) => {
                    const polygon = getPolygonPositions(area?.polygon);
                    if (polygon.length < 3) return null;
                    const isActive = String(area.id) === String(selectedAreaId);
                    const worstHealthState = getWorstHealthState(getAreaTrees(area));
                    const areaColor = HEALTH_COLOR_BY_STATE[worstHealthState] || GREY;
                    return (
                      <Polygon
                        key={`poly-${area.id}`}
                        positions={polygon}
                        pathOptions={{
                          color: areaColor,
                          weight: isActive ? 4 : 2,
                          fillColor: areaColor,
                          fillOpacity: isActive ? 0.24 : 0.12,
                        }}
                        eventHandlers={{
                          click: () => setSelectedAreaId(area.id),
                        }}
                      >
                        <Popup>
                          <strong>{area.name}</strong>
                        </Popup>
                      </Polygon>
                    );
                  })}

                  {filteredAreas.flatMap((area) =>
                    getAreaTrees(area).map((tree) => {
                      const pos = toLatLng(tree?.position);
                      if (!pos) return null;
                      const hasSensor =
                        Array.isArray(tree?.installations) && tree.installations.length > 0;
                      return (
                        <Marker
                          key={`tree-${area.id}-${tree.id}`}
                          position={pos}
                          icon={hasSensor ? parentTreeIcon(tree?.health_state) : noSensorTreeIcon()}
                        >
                          <Popup>
                            <div className="d-flex flex-column gap-1">
                              <strong>{tree?.name || `${t("tree")} #${tree?.id}`}</strong>
                              <small className="text-muted">{area?.name}</small>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary mt-1"
                                onClick={() => navigate(`/trees?selectedTree=${tree.id}`)}
                              >
                                {t("view_on_list")}
                              </button>
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })
                  )}
                </MapContainer>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
